// @ts-check

/**
 * @file Provides the shared centered modal shell.
 */

import {PortalHost} from "conjointment"
import {memo, useMemo} from "react"
import usePressOutsideProps from "outside-eye/build/use-press-outside-props.js"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {PanResponder} from "react-native"
import {Modal, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {GestureResponderEvent, PanResponderGestureState} from "react-native" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} ModalProps
 * @property {string} [backdropTestID] Stable test id for the backdrop press target.
 * @property {object | object[]} [cardStyle] Extra card layout style.
 * @property {string} [cardTestID] Stable test id for the card.
 * @property {ReactNode} [children] Modal body.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {() => unknown} [onDismiss] Callback fired after the modal finishes dismissing.
 * @property {() => unknown} onRequestClose Callback fired for backdrop and platform close gestures.
 * @property {object | object[]} [overlayStyle] Extra overlay layout style.
 * @property {string} [testID] Stable test id for the modal overlay.
 */

/** @typedef {Record<string, never>} ModalState */

/**
 * Shared centered modal shell with backdrop and platform close handling.
 * @augments {ShapeComponent<ModalProps, ModalState>}
 */
class SharedModal extends ShapeComponent {
  static propTypes = PropTypesExact({
    backdropTestID: PropTypes.string,
    cardStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    cardTestID: PropTypes.string,
    children: PropTypes.any,
    isVisible: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired,
    overlayStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    testID: PropTypes.string
  })
  static defaultProps = {
    backdropTestID: undefined,
    cardStyle: undefined,
    cardTestID: undefined,
    children: null,
    onDismiss: undefined,
    overlayStyle: undefined,
    testID: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof PanResponder.create> | null} */
  backdropPanResponder = null
  /** @type {ReturnType<typeof usePressOutsideProps>} */
  pressOutsideProps = {}
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the modal shell. */
  setup() {
    this.backdropPanResponder ||= PanResponder.create({
      onPanResponderRelease: this.onBackdropResponderRelease,
      onStartShouldSetPanResponder: this.onBackdropStartShouldSetResponder
    })
    this.colors = useThemeColors()
    this.pressOutsideProps = usePressOutsideProps()
    this.themeName = useThemeName()
  }

  /**
   * Renders the modal shell.
   * @returns {ReactNode} Modal wrapper.
   * @throws {Error} When setup did not initialize the backdrop responder.
   */
  render() {
    const {backdropTestID, cardStyle, cardTestID, children, overlayStyle, testID} = this.props
    const backdropPanResponder = this.tt.backdropPanResponder
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const rootStyle = useMemo(() => {
      const baseStyle = styles[`overlay-${themeName}`] ||= {
        alignItems: "center",
        backgroundColor: colors.overlay,
        bottom: 0,
        flex: 1,
        justifyContent: "center",
        left: 0,
        padding: 24,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 100
      }

      return overlayStyle ? [baseStyle, overlayStyle] : baseStyle
    }, [colors, overlayStyle, themeName])
    const modalCardStyle = useMemo(() => {
      const baseStyle = styles[`card-${themeName}`] ||= {
        backgroundColor: colors.surfaceRaised,
        borderColor: colors.border,
        borderRadius: 16,
        borderWidth: 1,
        padding: 20,
        width: "100%",
        zIndex: 1
      }

      return cardStyle ? [baseStyle, cardStyle] : baseStyle
    }, [cardStyle, colors, themeName])
    if (!backdropPanResponder) throw new Error("Expected backdrop pan responder before rendering shared modal")

    return (
      <Modal
        animationType="fade"
        onDismiss={this.tt.onDismiss}
        onRequestClose={this.tt.onRequestClose}
        transparent
        visible={this.p.isVisible}
      >
        <View
          {...this.pressOutsideProps}
          style={rootStyle}
          testID={testID}
        >
          <View
            {...backdropPanResponder.panHandlers}
            style={styles.backdrop ||= {
              bottom: 0,
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 0
            }}
            testID={backdropTestID || (testID ? `${testID}/backdrop` : undefined)}
          />
          <PortalHost
            name="base"
            placement="above"
          >
            <View
              style={modalCardStyle}
              testID={cardTestID || (testID ? `${testID}/card` : undefined)}
            >
              {this.p.isVisible ? children : null}
            </View>
          </PortalHost>
        </View>
      </Modal>
    )
  }

  /**
   * Allows the backdrop view to receive touch and mouse starts.
   * @returns {boolean} Always true for the dedicated backdrop view.
   */
  onBackdropStartShouldSetResponder = () => true

  /**
   * Closes the modal after a backdrop release.
   * @param {GestureResponderEvent} _event Responder event.
   * @param {PanResponderGestureState} _gestureState Gesture state.
   * @returns {void} Nothing.
   */
  onBackdropResponderRelease = (_event, _gestureState) => {
    this.p.onRequestClose()
  }

  /**
   * Forwards native modal close gestures to the shared close callback.
   * @returns {void} Nothing.
   */
  onRequestClose = () => {
    this.p.onRequestClose()
  }

  /**
   * Clears retained children after the native modal finishes dismissing.
   * @returns {void} Nothing.
   */
  onDismiss = () => {
    if (this.p.isVisible) return
    if (this.p.onDismiss) {
      this.p.onDismiss()
    }
  }
}

/** @type {NamedExoticComponent<ModalProps>} */
const ModalComponent = memo(shapeComponent(SharedModal))

export default ModalComponent
